import { TreeNode } from './tree-checklist.types';

const isLeaf = (node: TreeNode): boolean => !node.children || node.children.length === 0;
const isSelectableLeaf = (node: TreeNode): boolean => isLeaf(node) && node.selectable !== false && !node.disabled;

/** All selectable (non-disabled) leaf ids under the given nodes, in order. */
export const getSelectableLeafIds = (nodes: TreeNode[]): string[] =>
  nodes.flatMap((node) =>
    isLeaf(node) ? (isSelectableLeaf(node) ? [node.id] : []) : getSelectableLeafIds(node.children!)
  );

/** checked/indeterminate for a node given the selected set. Groups roll up their selectable leaves. */
export const getNodeState = (node: TreeNode, selected: Set<string>): { checked: boolean; indeterminate: boolean } => {
  if (isLeaf(node)) {
    return { checked: selected.has(node.id), indeterminate: false };
  }
  const leafIds = getSelectableLeafIds([node]);
  const selectedCount = leafIds.filter((id) => selected.has(id)).length;
  const checked = leafIds.length > 0 && selectedCount === leafIds.length;
  const indeterminate = selectedCount > 0 && selectedCount < leafIds.length;
  return { checked, indeterminate };
};

/** Toggle a node against the current selection, returning the next selection array. */
export const toggleNode = (node: TreeNode, selected: string[]): string[] => {
  if (isLeaf(node)) {
    if (!isSelectableLeaf(node)) return selected;
    return selected.includes(node.id) ? selected.filter((id) => id !== node.id) : [...selected, node.id];
  }
  const leafIds = getSelectableLeafIds([node]);
  const leafSet = new Set(leafIds);
  const selectedSet = new Set(selected);
  const allSelected = leafIds.length > 0 && leafIds.every((id) => selectedSet.has(id));
  if (allSelected) {
    return selected.filter((id) => !leafSet.has(id));
  }
  const next = [...selected];
  leafIds.forEach((id) => {
    if (!selectedSet.has(id)) next.push(id);
  });
  return next;
};

/** Filter the tree by a case-insensitive label search. A group survives if it matches (keep all children)
 *  or any child matches (keep matching children). Empty term returns the tree unchanged. */
export const filterTreeBySearch = (nodes: TreeNode[], term: string): TreeNode[] => {
  const q = term.trim().toLowerCase();
  if (!q) return nodes;
  return nodes.reduce<TreeNode[]>((acc, node) => {
    const selfMatches = node.label.toLowerCase().includes(q);
    if (isLeaf(node)) {
      if (selfMatches) acc.push(node);
      return acc;
    }
    if (selfMatches) {
      acc.push(node);
      return acc;
    }
    const children = filterTreeBySearch(node.children!, term);
    if (children.length) acc.push({ ...node, children });
    return acc;
  }, []);
};

/** Depth-first lookup of a node's label by id. */
export const findLabel = (nodes: TreeNode[], id: string): string | undefined => {
  for (const node of nodes) {
    if (node.id === id) return node.label;
    if (node.children) {
      const found = findLabel(node.children, id);
      if (found) return found;
    }
  }
  return undefined;
};
